import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, CheckCheck, Loader2, ShieldCheck, XCircle, Info, MailCheck, Settings2, Download, Printer } from "lucide-react";
import { listMyNotifications, markNotificationsRead, undoMarkNotificationsRead, exportMyNotificationsCsv } from "@/lib/notifications.functions";
import { getKycEventPrefs, updateKycEventPrefs } from "@/lib/helper-kyc.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — Peopels" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NotificationsPage,
});

type NotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  data: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
};

const PAGE_SIZE = 25;

function iconFor(type: string) {
  if (type === "kyc_decision") return <ShieldCheck className="h-5 w-5 text-emerald-600" />;
  if (type === "kyc_submitted") return <MailCheck className="h-5 w-5 text-sky-600" />;
  if (type.includes("reject") || type.includes("fail")) return <XCircle className="h-5 w-5 text-rose-600" />;
  return <Info className="h-5 w-5 text-slate-500" />;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

function NotificationsPage() {
  const list = useServerFn(listMyNotifications);
  const markRead = useServerFn(markNotificationsRead);
  const undoRead = useServerFn(undoMarkNotificationsRead);
  const exportCsv = useServerFn(exportMyNotificationsCsv);
  const qc = useQueryClient();

  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  // Per-click guard: blocks double-submits while the server is processing.
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["my-notifications", page, filter, typeFilter],
    queryFn: () => list({ data: { limit: PAGE_SIZE, offset: page * PAGE_SIZE, filter, type: typeFilter } }),
    refetchOnWindowFocus: true,
  });

  const markMut = useMutation({
    mutationFn: (vars: { ids?: string[]; all?: boolean; type?: string }) => markRead({ data: vars }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["my-notifications"] });
      const res = r as { updated: number; ids: string[] };
      if (res.updated > 0) {
        toast.success(`Marked ${res.updated} as read`, {
          action: res.ids.length
            ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    await undoRead({ data: { ids: res.ids } });
                    toast.success("Undone");
                    qc.invalidateQueries({ queryKey: ["my-notifications"] });
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : "Could not undo");
                  }
                },
              }
            : undefined,
          duration: 8000,
        });
      } else {
        toast.message("Nothing to mark — already up to date");
      }
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not mark read"),
    onSettled: () => setBulkBusy(false),
  });

  const handleBulkMark = (vars: { ids?: string[]; all?: boolean; type?: string }) => {
    if (bulkBusy || markMut.isPending) return;
    setBulkBusy(true);
    markMut.mutate(vars);
  };

  const handleExportCsv = async () => {
    try {
      const r = await exportCsv({ data: { filter, type: typeFilter === "all" ? undefined : typeFilter } });
      const res = r as { csv: string; filename: string; count: number };
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.count} row${res.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    }
  };


  const items = (data?.items ?? []) as NotificationRow[];
  const unread = data?.unreadCount ?? 0;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-900/5 p-2.5">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Notifications</h1>
              <p className="text-sm text-muted-foreground">
                {unread > 0 ? `${unread} unread` : "All caught up"} · {total} total
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              title="Download a CSV with the current filters and sort applied"
            >
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              title="Print or save as PDF — uses the current filters"
            >
              <Printer className="mr-2 h-4 w-4" /> PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!unread || bulkBusy || markMut.isPending}
              onClick={() => handleBulkMark({ all: true, type: typeFilter === "all" ? undefined : typeFilter })}
              title={typeFilter !== "all" ? `Mark all ${typeFilter} as read` : "Mark all as read"}
            >
              {bulkBusy || markMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCheck className="mr-2 h-4 w-4" />}
              {typeFilter !== "all" ? `Mark all ${typeFilter} read` : "Mark all read"}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
          <select
            value={filter}
            onChange={(e) => { setPage(0); setFilter(e.target.value as "all" | "unread"); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="all">All</option>
            <option value="unread">Unread only</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setPage(0); setTypeFilter(e.target.value); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="all">All types</option>
            <option value="kyc_decision">KYC decisions</option>
            <option value="kyc_submitted">KYC submitted</option>
          </select>
          {items.length > 0 && items.some((i) => !i.read_at) ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={bulkBusy || markMut.isPending}
              onClick={() => handleBulkMark({ ids: items.filter((i) => !i.read_at).map((i) => i.id) })}
            >
              Mark this page read
            </Button>
          ) : null}
        </div>


        <KycPrefsPanel />

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border bg-white p-10 text-center text-sm text-muted-foreground">
            You don't have any notifications yet.
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {items.map((n) => {
                const isUnread = !n.read_at;
                const content = (
                  <div
                    className={`flex gap-3 rounded-2xl border p-4 transition ${
                      isUnread ? "border-slate-900/10 bg-white shadow-sm" : "border-transparent bg-slate-50/60"
                    }`}
                  >
                    <div className="mt-0.5">{iconFor(n.type)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <p className={`truncate text-sm ${isUnread ? "font-semibold" : "font-medium"}`}>{n.title}</p>
                        <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                      </div>
                      {n.body ? (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{n.body}</p>
                      ) : null}
                    </div>
                    {isUnread ? <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" /> : null}
                  </div>
                );
                return (
                  <li key={n.id}>
                    {n.link ? (
                      <a
                        href={n.link}
                        onClick={() => { if (isUnread) markMut.mutate({ ids: [n.id] }); }}
                        className="block"
                      >
                        {content}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => isUnread && markMut.mutate({ ids: [n.id] })}
                        className="w-full text-left"
                      >
                        {content}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            {totalPages > 1 ? (
              <div className="mt-6 flex items-center justify-between gap-3">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

type KycPrefs = {
  kyc_inapp_approved: boolean;
  kyc_inapp_rejected: boolean;
  kyc_inapp_more_info: boolean;
  kyc_email_approved: boolean;
  kyc_email_rejected: boolean;
  kyc_email_more_info: boolean;
};

function KycPrefsPanel() {
  const get = useServerFn(getKycEventPrefs);
  const save = useServerFn(updateKycEventPrefs);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["kyc-event-prefs"], queryFn: () => get() });
  const [local, setLocal] = useState<KycPrefs | null>(null);

  useEffect(() => { if (data?.prefs) setLocal(data.prefs as KycPrefs); }, [data]);

  const mut = useMutation({
    mutationFn: (p: KycPrefs) => save({ data: p }),
    onSuccess: () => { toast.success("Preferences saved"); qc.invalidateQueries({ queryKey: ["kyc-event-prefs"] }); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not save"),
  });

  if (isLoading || !local) {
    return (
      <div className="mb-6 rounded-2xl border bg-white p-6 text-sm text-muted-foreground">
        <Loader2 className="inline h-4 w-4 animate-spin" /> Loading verification preferences…
      </div>
    );
  }

  const rows: Array<{ key: "approved" | "rejected" | "more_info"; label: string; help: string }> = [
    { key: "approved", label: "Approved", help: "When your verification is approved" },
    { key: "more_info", label: "More info required", help: "When the reviewer needs more documents" },
    { key: "rejected", label: "Rejected", help: "When your verification is rejected" },
  ];

  const update = (k: keyof KycPrefs, v: boolean) => setLocal((p) => (p ? { ...p, [k]: v } : p));

  return (
    <details className="mb-6 rounded-2xl border bg-white shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold">Verification notification preferences</p>
            <p className="text-xs text-muted-foreground">Choose in-app and email alerts per outcome.</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">Open</span>
      </summary>
      <div className="border-t p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 text-left">Event</th>
              <th className="py-2 text-center">In-app</th>
              <th className="py-2 text-center">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const inappKey = `kyc_inapp_${r.key}` as keyof KycPrefs;
              const emailKey = `kyc_email_${r.key}` as keyof KycPrefs;
              return (
                <tr key={r.key} className="border-t">
                  <td className="py-3">
                    <div className="font-medium">{r.label}</div>
                    <div className="text-xs text-muted-foreground">{r.help}</div>
                  </td>
                  <td className="py-3 text-center">
                    <Switch checked={local[inappKey]} onCheckedChange={(v) => update(inappKey, v)} />
                  </td>
                  <td className="py-3 text-center">
                    <Switch checked={local[emailKey]} onCheckedChange={(v) => update(emailKey, v)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={() => mut.mutate(local)} disabled={mut.isPending}>
            {mut.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            Save preferences
          </Button>
        </div>
      </div>
    </details>
  );
}
